import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { ToastService } from '../../../services/toast.service';
import { firstValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { CheckboxModule } from 'primeng/checkbox';
import { RippleModule } from 'primeng/ripple';
import { AutoFocusModule } from 'primeng/autofocus';
import { DrawerModule } from 'primeng/drawer';
import { environment } from 'src/environments/environment';
import { DividerModule } from 'primeng/divider';
import { firebaseAuthTranslation } from 'src/app/util/FirebaseErrorTranslation';
import { PASSWORD_VALIDATE_REGEX, hasUppercase, hasLowercase, hasMinLength } from 'src/app/util/PasswordUtil';
import { validatePasswordMatch } from 'src/app/util/Validators';
import { MessageModule } from 'primeng/message';

@Component({
  selector: 'app-login',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
  standalone: true,
  imports: [
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    PasswordModule,
    FormsModule,
    RouterModule,
    RippleModule,
    ReactiveFormsModule,
    AutoFocusModule,
    DrawerModule,
    CommonModule,
    DividerModule,
    MessageModule
  ],
})
export class LoginPage implements OnInit {
  isLoginInitiated = false;
  isPasswordResetRequestVisible = false;
  isPasswordResetRequestInitiated = false;
  environment = environment;

  isPasswordResetVisible = false;
  isPasswordResetInitiated = false;

  // Password visibility toggles
  showPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  loginForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required]),
  });

  passwordResetRequestForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
  });

  passwordResetForm = new FormGroup(
    {
      password: new FormControl('', [
        Validators.required,
        Validators.pattern(PASSWORD_VALIDATE_REGEX),
      ]),
      passwordConfirm: new FormControl('', [
        Validators.required,
        Validators.pattern(PASSWORD_VALIDATE_REGEX),
      ]),
    },
    {
      validators: validatePasswordMatch,
    },
  );

  hasUppercase = hasUppercase;
  hasLowercase = hasLowercase;
  hasMinLength = hasMinLength;

  constructor(
    private _auth: AuthService,
    private _router: Router,
    private _toast: ToastService,
    private _route: ActivatedRoute,
  ) { }

  async ngOnInit() {
    const mode = this._route.snapshot.queryParams['mode'];
    if (mode == 'resetPassword') {
      this.isPasswordResetVisible = true;
    } else if (mode == 'verifyEmail') {
      this.verifyEmail();
    } else {
      if (this._auth.user$) {
        const user = await firstValueFrom(this._auth.user$);
        if (user) {
          // Check for returnUrl in query parameters
          const returnUrl = this._route.snapshot.queryParams['returnUrl'];
          if (returnUrl) {
            this._router.navigateByUrl(returnUrl);
          } else {
            this._router.navigate(['/dashboard']);
          }
        }
      }
    }
  }


  async performStandardLogin() {
    if (this.loginForm.invalid) {
      // Mark all form controls as touched to trigger validation visuals
      Object.keys(this.loginForm.controls).forEach((key) => {
        const control: any = this.loginForm.get(key);
        control.markAsTouched();
        control.markAsDirty();
      });
      this._toast.add({
        severity: 'contrast',
        summary: 'Bitte alle Felder ausfüllen',
      });
      return;
    }
    this.isLoginInitiated = true;
    this.loginForm.disable();

    try {
      await firstValueFrom(
        this._auth.login(
          this.loginForm.value.email!,
          this.loginForm.value.password!,
        ),
      );

      // Check for returnUrl in query parameters
      const returnUrl = this._route.snapshot.queryParams['returnUrl'];
      if (returnUrl) {
        this._router.navigateByUrl(returnUrl);
      } else {
        this._router.navigate(['/dashboard']);
      }
    } catch (error: any) {

      const errorTranslation = firebaseAuthTranslation[error.code];

      if (errorTranslation?.toDev) console.error(errorTranslation.toDev);
      this._toast.add({
        severity: 'contrast',
        summary: 'Fehler beim Login',
        detail: errorTranslation?.toClient ?? 'Interner Serverfehler',
      });
    } finally {
      this.isLoginInitiated = false;
      this.loginForm.enable();

    }
  }

  async performPasswordResetRequest() {
    if (this.passwordResetRequestForm.invalid) {
      return;
    }
    this.isPasswordResetRequestInitiated = true;
    this.passwordResetRequestForm.disable();

    try {
      await firstValueFrom(
        this._auth.performPasswordReset(
          this.passwordResetRequestForm.value.email!,
        ),
      );
      this.isPasswordResetRequestVisible = false;
      this.isPasswordResetRequestInitiated = false;
      this.passwordResetRequestForm.enable();
      this._toast.add({
        severity: 'success',
        summary: 'Passwort zurücksetzen',
        detail:
          'Dir wurde eine E-Mail zum Zurücksetzen deines Passworts gesendet.',
      });
    } catch (error: any) {
      this.isPasswordResetRequestInitiated = false;
      this.passwordResetRequestForm.enable();

      const errorTranslation = firebaseAuthTranslation[error.code];

      if (errorTranslation?.toDev) console.error(errorTranslation.toDev);
      this._toast.add({
        severity: 'contrast',
        summary: 'Fehler beim Zurücksetzen des Passworts',
        detail: errorTranslation?.toClient ?? 'Interner Serverfehler',
      });
    }
  }

  async performPasswordReset() {
    if (this.passwordResetForm.invalid) {
      return;
    }

    this.isPasswordResetInitiated = true;
    this.passwordResetForm.disable();
    const oobCode = this._route.snapshot.queryParams['oobCode'];

    try {
      await firstValueFrom(
        this._auth.performPasswordResetWithCode(
          oobCode,
          this.passwordResetForm.value.password!,
        ),
      );
      this.isPasswordResetVisible = false;
      this._toast.add({
        severity: 'success',
        summary: 'Passwort zurücksetzen',
        detail:
          'Dein Passwort wurde erfolgreich zurückgesetzt, logge dich mit deinem neuen Passwort ein.',
      });
    } catch (error: any) {
      const errorTranslation = firebaseAuthTranslation[error.code];
      if (errorTranslation?.toDev) console.error(errorTranslation.toDev);
      this._toast.add({
        severity: 'contrast',
        summary: 'Fehler beim Zurücksetzen des Passworts',
        detail: errorTranslation?.toClient ?? 'Interner Serverfehler',
      });
    } finally {
      this.isPasswordResetVisible = false;
      this.isPasswordResetInitiated = false;
      this.passwordResetForm.enable();
      this._router.navigate([], {
        queryParams: {
          mode: null,
          oobCode: null,
          lang: null,
          apiKey: null,
        },
        queryParamsHandling: 'merge',
      });
    }
  }

  async verifyEmail() {
    const oobCode = this._route.snapshot.queryParams['oobCode'];
    this.isLoginInitiated = true;

    try {
      await firstValueFrom(this._auth.performEmailVerification(oobCode));
      this._toast.add({
        severity: 'success',
        summary: 'E-Mail verifiziert',
        detail: 'Deine E-Mail wurde erfolgreich verifiziert.',
      });

      setTimeout(() => {
        this._router.navigate(['/onboarding']);
      }, 2000);
    } catch (error: any) {
      const errorTranslation = firebaseAuthTranslation[error.code];
      if (errorTranslation?.toDev) console.error(errorTranslation.toDev);
      this._toast.add({
        severity: 'contrast',
        summary: 'Fehler bei der E-Mail-Verifizierung',
        detail: errorTranslation?.toClient ?? 'Interner Serverfehler',
      });
      this._router.navigate([], {
        queryParams: {
          mode: null,
          oobCode: null,
          lang: null,
          apiKey: null,
        },
        queryParamsHandling: 'merge',
      });
      this.isLoginInitiated = false;
    }
  }
}
